"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils/cn";
import { CDN_ASSETS } from "@/lib/cdn";
import { SidebarUserButton } from "@/components/ui/sidebar-user";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { useCustomOrderPermissions } from "@/hooks/useCustomOrderPermissions";
import BreadcrumbsWrapper from "@/components/breadcrumbs/BreadcrumbsWrapper";
import { NotificationPanel } from "@/components/notifications/notification-panel";
import { notificationApi, useGetNotificationsQuery } from "@/store/api/notificationApi";
import { enrichmentProgressReceived } from "@/store/slices/enrichmentProgressSlice";
import {
  workflowEventReceived,
  workflowResynced,
  type WorkflowProgressState,
} from "@/store/slices/workflowProgressSlice";
import { workflowApi } from "@/store/api/workflowApi";
import { enrichmentApi } from "@/store/api/enrichmentApi";
import type { EnrichmentProgressPush } from "@/lib/types/enrichment.types";
import { setEventStreamConnected } from "@/hooks/useEventStreamConnected";
import { useWorkspaceBulkEvents } from "@/hooks/workspace/useWorkspaceBulkEvents";
import { useWorkspaceBulkPhoneEvents } from "@/hooks/workspace/useWorkspaceBulkPhoneEvents";
import { useRadarBulkUnlockEvents } from "@/hooks/people-search/useRadarBulkUnlockEvents";
import { useBulkBatchProgressEvents } from "@/hooks/useBulkBatchProgressEvents";
import { useBulkBatchTracking } from "@/hooks/useBulkBatchTracking";
import {
  phoneWorkflowEventReceived,
  phoneWorkflowResynced,
  type PhoneWorkflowProgressState,
} from "@/store/slices/phoneWorkflowProgressSlice";
import { phoneWorkflowApi } from "@/store/api/phoneWorkflowApi";
import { useAppDispatch, store } from "@/store/store";
import api from "@/lib/axios";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  Home,
  UserSearch,
  List,
  Database,
  Bell,
  Settings,
  X,
  Users,
  Building2,
  ShieldAlert,
  Mail,
  UserX,
  Globe,
  LayoutGrid,
  PackageSearch,
  FilePlus2,
  ClipboardList,
  ClipboardCheck,
} from "lucide-react";

type NavItemChild = {
  href: string;
  label: string;
  icon?: React.ReactNode;
  /** Needs the Custom Order `manage` permission (the data team); shown disabled to everyone else. */
  dataTeamOnly?: boolean;
};

type NavItem = {
  label: string;
  icon: React.ReactNode;
  href?: string;
  children?: NavItemChild[];
};

/**
 * IMPORTANT:
 *
 * Companies is intentionally NOT a top-level sidebar item.
 * It belongs under Search.
 */
const navItems: NavItem[] = [
  {
    href: "/home",
    label: "Home",
    icon: <Home className="h-4.5 w-4.5" />,
  },

  {
    label: "Search",
    icon: <UserSearch className="h-4.5 w-4.5" />,
    children: [
      {
        href: "/search/people",
        label: "People",
        icon: <Users className="h-4 w-4" />,
      },
{
    href: "/search/companies",
    label: "Companies",
    icon: <Building2 className="h-4 w-4" />,
  },
    ],
  },

  {
    href: "/workspace",
    label: "Workspace",
    icon: <LayoutGrid className="h-4.5 w-4.5" />,
  },

  {
    label: "Lists",
    icon: <List className="h-4.5 w-4.5" />,
    children: [
      {
        href: "/lists/contacts",
        label: "Contacts",
        icon: <Users className="h-4 w-4" />,
      },
      {
        href: "/lists/email-suppression",
        label: "Email Suppression",
        icon: <Mail className="h-4 w-4" />,
      },
      {
        href: "/lists/name-suppression",
        label: "Name Suppression",
        icon: <UserX className="h-4 w-4" />,
      },
      {
        href: "/lists/website-suppression",
        label: "Website Suppression",
        icon: <Globe className="h-4 w-4" />,
      },
    ],
  },

  {
    href: "/enrichment/csv-enrichment",
    label: "Enrichment",
    icon: <Database className="h-4.5 w-4.5" />,
  },

  {
    label: "Custom Data",
    icon: <PackageSearch className="h-4.5 w-4.5" />,
    children: [
      {
        href: "/custom-data/new",
        label: "New Order",
        icon: <FilePlus2 className="h-4 w-4" />,
      },
      {
        href: "/custom-data/orders",
        label: "My Orders",
        icon: <ClipboardList className="h-4 w-4" />,
      },
      {
        href: "/custom-data/manage",
        label: "Manage Orders",
        icon: <ClipboardCheck className="h-4 w-4" />,
        dataTeamOnly: true,
      },
    ],
  },
];

const isSectionActive = (
  item: NavItem,
  pathname: string
): boolean => {
  if (item.href) {
    if (item.href === "/") {
      return pathname === "/";
    }

    return (
      pathname === item.href ||
      pathname.startsWith(item.href + "/")
    );
  }

  if (item.children?.length) {
    return item.children.some(
      (child) =>
        pathname === child.href ||
        pathname.startsWith(child.href + "/")
    );
  }

  return false;
};

export function Sidebar({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();

  const [submoduleSheetOpen, setSubmoduleSheetOpen] =
    useState(false);

  const [activeSubmoduleItem, setActiveSubmoduleItem] =
    useState<string | null>(null);

  const [activityOpen, setActivityOpen] = useState(false);

  // True once the live notification stream has exhausted its reconnect
  // budget below. Drives the amber dot on the activity bell; clicking the
  // bell while set retries immediately via streamRetryRef.
  const [isStreamDown, setIsStreamDown] = useState(false);
  const streamRetryRef = useRef<() => void>(() => {});

  const dispatch = useAppDispatch();

  // Workspace bulk-email SSE events (one debounced rows refresh per batch,
  // header progress, external-run adoption) — the stream handler below offers
  // every workflow.progress event to it first.
  const handleBulkWorkflowEvent = useWorkspaceBulkEvents();
  const handleBulkPhoneEvent = useWorkspaceBulkPhoneEvents();
  // The aggregate batch-level event (see useBulkBatchProgressEvents). Separate
  // from the three per-workflow handlers above because it arrives under its
  // own SSE `type`. Scoped to People Search: Workspace's own bulk runs are
  // deliberately not wired to it yet (see that hook's note).
  const handleBulkBatchProgress = useBulkBatchProgressEvents();
  // Mounted here rather than in the notification panel so the bell can show a
  // running batch without the panel ever being opened, and so a reload
  // mid-batch still finds it. Idle cost is two requests per page load; it only
  // starts polling once something is actually running.
  const { runningCount: runningBatchCount } = useBulkBatchTracking();
  // People Search bulk unlock (see useBulkContactUnlock.ts) - counts finishes
  // per batch and claims the event, same shape as the two above.
  const handleRadarBulkUnlockEvent = useRadarBulkUnlockEvents();

  // Push does the real-time work; this poll is only a backstop in case a
  // push was missed while the stream below was reconnecting.
  const { data: notificationsData, isLoading: notificationsLoading } = useGetNotificationsQuery(
    { page: 1, limit: 10 },
    { pollingInterval: 120000 }
  );
  const unreadCount = notificationsData?.unreadCount ?? 0;
  /**
   * What the bell's badge counts. This control is an ACTIVITY bell (see its
   * aria-label), and its panel holds both unread notifications and the Batch
   * Progress card — so a running background unlock is pending activity in
   * exactly the same sense an unread notification is, and hiding it behind an
   * unopened panel is how a 20-minute run ends up looking like nothing
   * happened. Counted together so one number answers "is anything waiting for
   * me", with the pulse below distinguishing "still working" from "unread".
   */
  const activityBadgeCount = unreadCount + runningBatchCount;

  // One live connection per browser tab for as long as the app shell is
  // mounted (i.e. for as long as the user is signed in). A "notification"
  // event just invalidates the tag above and lets RTK Query refetch the
  // authoritative page/count, rather than splicing the pushed payload into
  // the cache by hand. "enrichment.progress" is handled the same way: the CSV
  // enrichment screen reads its numbers from the uploads endpoint, and a push
  // only tells it to look again. "workflow.progress" is the exception — it is
  // written straight into workflowProgressSlice, because the People Search
  // unlock flow has no polling fallback and this one shared stream is its
  // only source of live data (see usePeopleActions.ts).
  useEffect(() => {
    let cancelled = false;
    let probing = false;
    let lastProbeAt = 0;
    const PROBE_COOLDOWN_MS = 15000;

    // Bounded reconnect with exponential backoff, owned here instead of the
    // browser. EventSource's native retry is unbounded, unthrottled, and blind
    // (it never sees the HTTP status), so a dead session or a down upstream
    // otherwise retries every ~3s forever while spamming the console. We close
    // the failed source in onerror and schedule our own attempt: 1s, 2s, 4s…
    // capped at 30s, up to MAX_RETRIES. Past that the stream is parked and the
    // bell shows an offline dot with click-to-retry, instead of looping quietly.
    const MAX_RETRIES = 10;
    const BASE_RETRY_DELAY_MS = 1000;
    const MAX_RETRY_DELAY_MS = 30000;
    let source: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let consecutiveFailures = 0;
    let hasLoggedDisconnect = false;

    const clearRetryTimer = () => {
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    };
    const closeSource = () => {
      if (source) {
        source.close();
        source = null;
      }
    };

    // enrichment.progress arrives once per 10-row chunk, and for a contact run
    // once more per contact as its email/phone waterfalls finish — ~11,000
    // pushes for a 10k-row file. Each one used to invalidate (and so refetch)
    // the whole uploads list. Pushes are now collected and the tags flushed at
    // most once per window; a push that finishes its run flushes immediately
    // so completion never waits on the timer.
    const ENRICHMENT_REFETCH_THROTTLE_MS = 3000;
    let enrichmentFlushTimer: ReturnType<typeof setTimeout> | null = null;
    let lastEnrichmentFlushAt = 0;
    const pendingEnrichmentStatsIds = new Set<string>();

    const clearEnrichmentFlushTimer = () => {
      if (enrichmentFlushTimer !== null) {
        clearTimeout(enrichmentFlushTimer);
        enrichmentFlushTimer = null;
      }
    };
    const flushEnrichmentInvalidation = () => {
      clearEnrichmentFlushTimer();
      if (cancelled) return;
      lastEnrichmentFlushAt = Date.now();
      const statsIds = [...pendingEnrichmentStatsIds];
      pendingEnrichmentStatsIds.clear();
      // Both tags, because two different screens are watching: the job list
      // and the progress screen read the uploads list, while the report's
      // find-similar panel reads /stats/:id. EnrichmentStats is tagged per id,
      // so it has to be invalidated by id — the bare "EnrichmentStats" string
      // would match nothing.
      dispatch(
        enrichmentApi.util.invalidateTags([
          "EnrichmentUploads",
          ...statsIds.map((id) => ({ type: "EnrichmentStats" as const, id })),
        ])
      );
    };
    // The service pushes before it writes `status: "completed"` on a contact
    // run (updateCompletedWorkflow), so the counts are checked too.
    const isFinalEnrichmentPush = (payload: EnrichmentProgressPush) =>
      payload.status === "completed" ||
      (payload.total > 0 && payload.processedcount >= payload.total) ||
      payload.similar_status === "completed" ||
      (!!payload.required_similar && (payload.similar_processed ?? 0) >= payload.required_similar);
    const scheduleEnrichmentInvalidation = (payload: EnrichmentProgressPush) => {
      if (payload.stats_id) pendingEnrichmentStatsIds.add(payload.stats_id);
      if (isFinalEnrichmentPush(payload)) {
        flushEnrichmentInvalidation();
        return;
      }
      if (enrichmentFlushTimer !== null) return;
      const wait = Math.max(0, lastEnrichmentFlushAt + ENRICHMENT_REFETCH_THROTTLE_MS - Date.now());
      enrichmentFlushTimer = setTimeout(flushEnrichmentInvalidation, wait);
    };

    // notifyWorkflowEvent's push to Notification-Service is fire-and-forget
    // with no retry - an event pushed while this connection was down (or
    // mid-reconnect) is dropped forever, which can leave a workflow's Redux
    // progress permanently stuck (e.g. a grid cell that never reveals its
    // unlocked value because it never saw workflow.finished). On every
    // RECONNECT (not the initial connect - nothing could have been missed
    // yet), re-fetch every workflow not yet completed and reconcile it, so a
    // dropped event self-heals instead of requiring a page reload.
    let hasConnectedBefore = false;
    const resyncInProgressWorkflows = async () => {
      // store's own reducer map is typed `as any` (see store.ts), so getState()
      // loses its shape here - reassert it just for this one read.
      const workflowProgress = (store.getState() as { workflowProgress: WorkflowProgressState })
        .workflowProgress;
      const ids = Object.values(workflowProgress.byWorkflowId)
        .filter((wf) => wf.completedAt === null)
        .map((wf) => wf.workflowId);
      for (const id of ids) {
        if (cancelled) return;
        try {
          const snapshot = await dispatch(workflowApi.endpoints.getWorkflow.initiate(id)).unwrap();
          if (!cancelled) dispatch(workflowResynced(snapshot));
        } catch (err) {
          console.warn(`[notifications] failed to resync workflow ${id} after reconnect`, err);
        }
      }
    };
    // Same self-healing resync, kept as its own loop against its own slice -
    // the phone-finding waterfall is a separate feature from the email one
    // above and must stay on separate state (see phoneWorkflowProgressSlice.ts).
    const resyncInProgressPhoneWorkflows = async () => {
      const phoneWorkflowProgress = (
        store.getState() as { phoneWorkflowProgress: PhoneWorkflowProgressState }
      ).phoneWorkflowProgress;
      const ids = Object.values(phoneWorkflowProgress.byWorkflowId)
        .filter((wf) => wf.completedAt === null)
        .map((wf) => wf.workflowId);
      for (const id of ids) {
        if (cancelled) return;
        try {
          const snapshot = await dispatch(phoneWorkflowApi.endpoints.getPhoneWorkflow.initiate(id)).unwrap();
          if (!cancelled) dispatch(phoneWorkflowResynced(snapshot));
        } catch (err) {
          console.warn(`[notifications] failed to resync phone workflow ${id} after reconnect`, err);
        }
      }
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      consecutiveFailures += 1;
      if (consecutiveFailures > MAX_RETRIES) {
        // Budget spent. Park the stream and surface it on the bell instead
        // of looping quietly; the next successful connect clears it.
        if (!cancelled) setIsStreamDown(true);
        return;
      }
      const delay = Math.min(
        BASE_RETRY_DELAY_MS * 2 ** (consecutiveFailures - 1),
        MAX_RETRY_DELAY_MS
      );
      clearRetryTimer();
      retryTimer = setTimeout(() => {
        if (!cancelled) connect();
      }, delay);
    };

    // EventSource never sees the HTTP status of a failed connection - a dead
    // session (expired cookie, revoked refresh token) looks identical here to
    // a transient network blip. Probe a plain JSON endpoint behind the same
    // `requireAuth` through the shared `api` instance instead: if the session
    // is genuinely dead, its response interceptor is the single source of
    // truth for signing out, so this piggybacks on that rather than
    // re-deciding it here. Throttled since each failed attempt re-fires
    // onerror.
    const probeSession = () => {
      if (cancelled || probing || Date.now() - lastProbeAt < PROBE_COOLDOWN_MS) return;
      probing = true;
      lastProbeAt = Date.now();
      api
        .get("/api/notification/notifications", { params: { page: 1, limit: 1 } })
        .catch(() => {
          // A dead session already triggered the sign-out flow inside the
          // interceptor. Anything else (offline, server restart) is exactly
          // the transient case the backoff above is meant to ride out.
        })
        .finally(() => {
          probing = false;
        });
    };

    const connect = () => {
      if (cancelled) return;
      clearRetryTimer();
      closeSource();
      const next = new EventSource("/api/notification/events/stream");
      source = next;

      next.onopen = () => {
        consecutiveFailures = 0;
        hasLoggedDisconnect = false;
        if (!cancelled) setIsStreamDown(false);
        // Published for screens that are driven by pushes, so they can drop
        // their fallback poll while this connection is carrying events.
        setEventStreamConnected(true);
        console.log("[notifications] event stream connected");
        if (hasConnectedBefore) {
          void resyncInProgressWorkflows();
          void resyncInProgressPhoneWorkflows();
        }
        hasConnectedBefore = true;
      };

      next.onmessage = (message) => {
      try {
        const parsed = JSON.parse(message.data);
        console.log("[notifications] event received", parsed);
        if (parsed?.type === "notification") {
          dispatch(notificationApi.util.invalidateTags(["Notification"]));
        } else if (parsed?.type === "enrichment.progress" && parsed?.payload) {
          // Treated as a "something changed, go and re-read it" trigger, the
          // same way a "notification" event is — the pushed payload is not
          // what the enrichment screens render. Delivery is best-effort and
          // the stream has no replay, so a push that goes missing would
          // otherwise pin a screen to whatever count arrived last; re-reading
          // the endpoint means a single delivered push resyncs everything.
          // Throttled — see scheduleEnrichmentInvalidation.
          scheduleEnrichmentInvalidation(parsed.payload);
          // Still recorded, but only for the `stats_id` it carries: the
          // progress screen mounts knowing just the userupload_id, and a push
          // resolves the stats_id sooner than the uploads list does.
          dispatch(enrichmentProgressReceived(parsed.payload));
        } else if (parsed?.type === "bulk.progress" && parsed?.payload) {
          // A bulk batch's own aggregate counters, pushed every 5 completions
          // plus once final (Workflow-Service's incrementBulkBatchCounts).
          // Its own SSE `type`, not nested inside "workflow.progress", so it
          // never reaches the per-workflow path below — `payload.workflowId`
          // here is the BATCH's id and a `getWorkflow` on it would 404.
          //
          // Only People Search consumes this today - the notification panel's
          // batch list and the bulk action bar's pills. Workspace's bulk runs
          // reach the same Workflow-Service batch endpoint (via
          // Enrichment-Service's /workspace/sendBulkEmail) and so emit this
          // event too, but nothing here acts on it yet; those events are
          // simply ignored.
          handleBulkBatchProgress(parsed.payload);
        } else if (parsed?.type === "workflow.progress" && parsed?.payload) {
          // Pushed by Infynd-CL-Workflow-Service as a person's email or
          // phone waterfall progresses. Both share this same top-level SSE
          // `type` (Notification-Service doesn't distinguish), so
          // `payload.category` is the one thing that tells them apart -
          // route into the matching slice rather than guessing from which
          // fields are present. usePeopleActions.ts reads whichever slice
          // this feeds (no separate per-workflow SSE connection anymore).
          const p = parsed.payload;

          console.log(
            `[unlock][sse] workflow event received workflowId=${p.workflowId} event=${p.event} rank=${p.currentRank ?? p.rank ?? "n/a"} category=${p.category ?? "email"}`
          );

          // Bulk email runs (Workspace sendBulkEmail) fan out server-side, so
          // every event of the batch carries `bulkBatchId` — and no grid cell
          // tracks those workflow ids. The hook turns finishes into header
          // progress plus one debounced rows refresh per batch (adopting runs
          // started outside this session); claiming the event here skips the
          // per-workflow GET resync below — one network call per row for data
          // nothing reads. Anything it doesn't claim (radar unlock, etc)
          // flows through the per-workflow path unchanged.
          // Checked before the Workspace handlers: a People Search bulk
          // unlock's rows are filled by one refetch per batch, so its events
          // must not reach the per-workflow path below at all (that path
          // fires a GET resync per finish - 50 requests for a 25-row batch,
          // for state no bulk row reads).
          if (handleRadarBulkUnlockEvent(p)) return;
          if (handleBulkWorkflowEvent(p)) return;
          if (handleBulkPhoneEvent(p)) return;

          if (parsed.payload.category === "phone") {
            dispatch(phoneWorkflowEventReceived(parsed.payload));
          } else {
            dispatch(workflowEventReceived(parsed.payload));
          }

          // Resync from the authoritative GET the instant the waterfall
          // reports done, rather than trusting the live event trail to be
          // complete.
          //
          // Confirmed against Infynd-CL-Workflow-Service's own source: when an
          // attempt times out mid-verification and the waterfall silently
          // advances to the next provider, NO event is emitted for that
          // attempt at all - not even attempt_late_result (that one only
          // fires if/when the async verification callback eventually arrives
          // for it). The attempt's own resolved email/verification is written
          // to Mongo at that moment, but the live SSE stream never mentions
          // it. `workflow.finished` itself carries no rank/providerKey to
          // recover this from either. So an attempts map built purely from
          // the events received so far can be missing a provider's real
          // outcome even after the workflow completes - which is exactly what
          // showed a provider that had actually found a guessed email as "Not
          // Found" in the modal. GET /api/workflows/:id reads the same Mongo
          // document this timeout path already wrote to, so it has the
          // correct per-provider data unconditionally - confirmed from a live
          // response earlier in this workflow's own lifetime, before it had
          // even finished.
          if (p.event === "workflow.finished" && p.workflowId) {
            if (p.category === "phone") {
              void dispatch(phoneWorkflowApi.endpoints.getPhoneWorkflow.initiate(p.workflowId))
                .unwrap()
                .then((snapshot) => {
                  if (!cancelled) dispatch(phoneWorkflowResynced(snapshot));
                })
                .catch((err) => {
                  // No longer expected for a retry-minted workflow: the
                  // service now persists /try and /try-all documents too, so
                  // this GET succeeds for them the same as any other workflow.
                  // The guard stays as a defensive catch-all, not a known case.
                  if ((err as { status?: number })?.status === 404) return;
                  console.warn(`[workflow] resync-on-finish failed for phone workflow ${p.workflowId}`, err);
                });
            } else {
              void dispatch(workflowApi.endpoints.getWorkflow.initiate(p.workflowId))
                .unwrap()
                .then((snapshot) => {
                  if (!cancelled) dispatch(workflowResynced(snapshot));
                })
                .catch((err) => {
                  // The service now persists /try and /try-all documents
                  // (manualTry: true), so this GET succeeds for a retry
                  // workflow the same as a normal one - this used to be an
                  // expected 404 for those; the guard stays as a defensive
                  // catch-all, not a known case.
                  if ((err as { status?: number })?.status === 404) return;
                  console.warn(`[workflow] resync-on-finish failed for email workflow ${p.workflowId}`, err);
                });
            }
          }
        }
      } catch {
        // Not JSON - nothing to do. (Heartbeat lines are SSE comments and
        // never reach onmessage at all, so this is only a defensive guard.)
      }
    };

      next.onerror = () => {
        if (cancelled) return;
        // Logged once per outage, not per attempt - the attempts themselves
        // are silent backoff ticks from here on.
        if (!hasLoggedDisconnect) {
          console.warn("[notifications] event stream disconnected, retrying with backoff...");
          hasLoggedDisconnect = true;
        }
        // Flip the shared flag straight away rather than waiting for the retry
        // budget to run out: a screen waiting on pushes has already stopped
        // receiving them, and its fallback poll should start now, not in the
        // several minutes the backoff can take to give up.
        setEventStreamConnected(false);
        // Take over from the native retry: close the failed source so it
        // stops hammering, then probe + schedule our own attempt.
        closeSource();
        probeSession();
        scheduleReconnect();
      };
    };

    // Manual retry for the bell's offline dot. Resets the budget so a tap is
    // always a genuine fresh attempt, even right after the budget ran out.
    streamRetryRef.current = () => {
      if (cancelled) return;
      consecutiveFailures = 0;
      hasLoggedDisconnect = false;
      setIsStreamDown(false);
      connect();
    };

    connect();

    return () => {
      cancelled = true;
      clearRetryTimer();
      clearEnrichmentFlushTimer();
      closeSource();
      setEventStreamConnected(false);
      console.log("[notifications] event stream closed");
    };
  }, [
    dispatch,
    handleBulkWorkflowEvent,
    handleBulkPhoneEvent,
    handleRadarBulkUnlockEvent,
    handleBulkBatchProgress,
  ]);

  const isSettingsActive =
    pathname?.startsWith("/settings");
  const isAdminActive = pathname?.startsWith("/admin");
  // Rendering-only: the real gate is requireSuperAdmin on every /api/admin route,
  // which 404s for everybody else. This just keeps the icon off a normal user's
  // sidebar. `null` (session not yet established) intentionally renders nothing.
  const isSuperAdmin = useIsSuperAdmin();
  // Rendering-only, like isSuperAdmin: /api/custom-data/manage enforces it.
  const { canManage: isDataTeam } = useCustomOrderPermissions();

  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );

  if (!mounted) {
    return <div className="min-h-screen" />;
  }

  /**
   * Open the submodule sheet.
   */
  const openSubmodules = (label: string) => {
    setActiveSubmoduleItem(label);
    setSubmoduleSheetOpen(true);
  };

  /**
   * Close the submodule sheet.
   */
  const closeSubmodules = () => {
    setSubmoduleSheetOpen(false);
    setActiveSubmoduleItem(null);
  };

  /**
   * Find currently selected sidebar item.
   */
  const activeItem = navItems.find(
    (item) => item.label === activeSubmoduleItem
  );

  return (
    <div className="flex min-h-screen">
      {/* ========================================================= */}
      {/* SIDEBAR                                                   */}
      {/* ========================================================= */}

      {/* pointer-events-auto is load-bearing: the panels that slide out of this
          rail (user menu, notifications) are modal Radix dialogs rendered
          without a backdrop, and a modal dialog puts pointer-events:none on
          <body> while open. Without this the rail still *looks* available --
          nothing dims it any more -- but every icon on it is dead, so you
          can't switch straight from one panel to another. */}
      <div
        className="
          pointer-events-auto
          fixed
          left-0
          top-0
          z-60
          flex
          h-screen
          w-18
          flex-col
          border-r
          border-sidebar-border
          bg-(--sidebar-bg)
          text-(--sidebar-text)
          shadow-lg
        "
      >
        {/* ===================================================== */}
        {/* HEADER                                                */}
        {/* ===================================================== */}

        <div className="flex flex-col items-center space-y-3 p-3">
          <Link
            href="/home"
            aria-label="Infynd Home"
            className="
              flex
              h-7.5
              w-7.5
              items-center
              justify-center
              rounded-lg
              transition-all
              duration-200
            "
          >
            <Image
              src={CDN_ASSETS.favicon}
              alt="Infynd"
              width={30}
              height={30}
              priority
            />
          </Link>
        </div>

        {/* ===================================================== */}
        {/* MAIN NAVIGATION                                       */}
        {/* ===================================================== */}

        <div className="flex-1 overflow-auto">
          <nav className="space-y-3">
            {navItems.map((item) => {
              const isActive = isSectionActive(
                item,
                pathname
              );

              const hasChildren =
                !!item.children?.length;

              return (
                <div key={item.label} className="relative">
                  {hasChildren ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() =>
                            openSubmodules(item.label)
                          }
                          className="
                            flex
                            w-full
                            cursor-pointer
                            select-none
                            flex-col
                            items-center
                            justify-center
                            gap-0.5
                            rounded-md
                            border-0
                            bg-transparent
                            p-0
                          "
                          aria-label={`Open ${item.label}`}
                        >
                          <div
                            className={cn(
                              "mx-auto flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-200 transition-colors duration-200",
                              isActive && "bg-gray-200"
                            )}
                          >
                            <span
                              className={cn(
                                "text-lg",
                                isActive
                                  ? "text-gray-900"
                                  : "text-gray-500 hover:text-gray-900"
                              )}
                            >
                              {item.icon}
                            </span>
                          </div>
                        </button>
                      </TooltipTrigger>

                      <TooltipContent side="right">
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          href={item.href ?? "#"}
                          className="
                            flex
                            w-full
                            cursor-pointer
                            flex-col
                            items-center
                            justify-center
                            gap-0.5
                            rounded-md
                          "
                          aria-label={item.label}
                        >
                          <div
                            className={cn(
                              "mx-auto flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-200 transition-colors duration-200",
                              isActive && "bg-gray-200"
                            )}
                          >
                            <span
                              className={cn(
                                "text-lg",
                                isActive
                                  ? "text-gray-900"
                                  : "text-gray-500 hover:text-gray-900"
                              )}
                            >
                              {item.icon}
                            </span>
                          </div>
                        </Link>
                      </TooltipTrigger>

                      <TooltipContent side="right">
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* ========================================================= */}
        {/* DIVIDER                                                   */}
        {/* ========================================================= */}

        <div className="my-2 h-px w-full bg-sidebar-border" />

        {/* ========================================================= */}
        {/* SETTINGS                                                 */}
        {/* ========================================================= */}

        <div className="space-y-3 py-3.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                href="/settings"
                className="
                  flex
                  w-full
                  cursor-pointer
                  flex-col
                  items-center
                  justify-center
                  gap-0.5
                "
                aria-label="Settings"
              >
                <div
                  className={cn(
                    "mx-auto flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-200 transition-colors",
                    isSettingsActive && "bg-gray-200"
                  )}
                >
                  <Settings
                    className={cn(
                      "h-4.5 w-4.5",
                      isSettingsActive
                        ? "text-gray-900"
                        : "text-gray-500 hover:text-gray-900"
                    )}
                  />
                </div>
              </Link>
            </TooltipTrigger>

            <TooltipContent side="right">
              Settings
            </TooltipContent>
          </Tooltip>

          {/* ===================================================== */}
          {/* SUPER ADMIN (internal only)                          */}
          {/* Hidden entirely for anyone without the flag - this is */}
          {/* cosmetic, not the security boundary. See useIsSuperAdmin. */}
          {/* ===================================================== */}

          {isSuperAdmin ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  href="/admin"
                  className="
                    flex
                    w-full
                    cursor-pointer
                    flex-col
                    items-center
                    justify-center
                    gap-0.5
                  "
                  aria-label="Super Admin"
                >
                  <div
                    className={cn(
                      "mx-auto flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-200 transition-colors",
                      isAdminActive && "bg-gray-200"
                    )}
                  >
                    <ShieldAlert
                      className={cn(
                        "h-4.5 w-4.5",
                        isAdminActive
                          ? "text-amber-600"
                          : "text-amber-500 hover:text-amber-600"
                      )}
                    />
                  </div>
                </Link>
              </TooltipTrigger>

              <TooltipContent side="right">
                Super Admin
              </TooltipContent>
            </Tooltip>
          ) : null}

          {/* ===================================================== */}
          {/* ACTIVITY                                             */}
          {/* ===================================================== */}

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
                  if (isStreamDown) streamRetryRef.current();
                  setActivityOpen(true);
                }}
                className="
                  flex
                  w-full
                  cursor-pointer
                  flex-col
                  items-center
                  justify-center
                  gap-0.5
                  border-0
                  bg-transparent
                  p-0
                "
                aria-label="Activity"
              >
                <div
                  className={cn(
                    "relative mx-auto flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-200",
                    activityOpen && "bg-gray-200"
                  )}
                >
                  {/* The badge hangs off the glyph, not the 40px hover box --
                      capped at 99+ so a big backlog can't stretch the rail. */}
                  <span className="relative inline-flex">
                    <Bell
                      className={cn(
                        "h-4.5 w-4.5",
                        activityOpen
                          ? "text-gray-900"
                          : "text-gray-500 hover:text-gray-900",
                        // A background bulk unlock runs for minutes with
                        // nothing else on screen to say so - the selection
                        // clears and there is no inline bar at that size. The
                        // bell is the only always-visible surface, so it
                        // reports the work rather than sitting still.
                        runningBatchCount > 0 && "animate-pulse text-brand"
                      )}
                    />
                    {activityBadgeCount > 0 ? (
                      <span
                        className={cn(
                          "absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[9px] font-semibold leading-none text-white",
                          runningBatchCount > 0 && "animate-pulse"
                        )}
                      >
                        {activityBadgeCount > 99 ? "99+" : activityBadgeCount}
                      </span>
                    ) : null}
                    {isStreamDown ? (
                      <span
                        className="absolute -left-1 -top-1 flex h-2.5 w-2.5"
                        title="Live updates paused — click to retry"
                      >
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
                      </span>
                    ) : null}
                  </span>
                </div>
              </button>
            </TooltipTrigger>

            <TooltipContent side="right">
              {isStreamDown ? "Activity — live updates paused, click to retry" : "Activity"}
            </TooltipContent>
          </Tooltip>

          {/* ===================================================== */}
          {/* USER                                                  */}
          {/* ===================================================== */}

          <SidebarUserButton isCollapsed={true} />
        </div>
      </div>

      {/* ========================================================= */}
      {/* SUBMODULE SHEET                                           */}
      {/* ========================================================= */}

      <Sheet
        open={submoduleSheetOpen}
        onOpenChange={(open) => {
          setSubmoduleSheetOpen(open);

          if (!open) {
            setActiveSubmoduleItem(null);
          }
        }}
      >
        {/* Same shape as sidebar-user.tsx -- see the note there for why each of
            translate-x-0!, sm:max-w-none and z-55 is load-bearing. This panel
            slides out beside the rail rather than over the page, so it drops
            the backdrop too: dimming everything behind it was wrong, and the
            z-70 overlay also covered the z-60 rail, so you couldn't click
            straight from one section's submodules to another's. */}
        <SheetContent
          side="left"
          showOverlay={false}
          className="
            fixed
            left-18!
            top-0
            z-55
            flex
            h-screen
            w-70
            min-w-70
            flex-col
            rounded-none
            border-r
            bg-background
            p-0
            shadow-xl
            sm:max-w-none
            [&]:translate-x-0!
            transition-opacity
          "
        >
          {/* ===================================================== */}
          {/* SHEET HEADER                                         */}
          {/* ===================================================== */}

          <SheetHeader
            className="
              flex
              flex-row
              items-center
              justify-between
              border-b
              px-4
              py-3
            "
          >
            <SheetTitle className="font-semibold">
              {activeItem?.label}
            </SheetTitle>

            <button
              type="button"
              onClick={closeSubmodules}
              className="
                cursor-pointer
                rounded-md
                p-1
                text-gray-400
                transition-colors
                hover:bg-muted
                hover:text-gray-600
              "
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </SheetHeader>

          {/* ===================================================== */}
          {/* SUBMODULE CONTENT                                    */}
          {/* ===================================================== */}

          <div className="flex-1 overflow-auto p-3">
            {activeItem?.children?.length ? (
              <div className="space-y-1">
                {activeItem.children.map((child) => {
                  const childActive =
                    pathname === child.href;

                  // Hide data team only items from non-data team users
                  if (child.dataTeamOnly && !isDataTeam) {
                    return null;
                  }

                  return (
                    <Link
                      key={child.href}
                      href={child.href}
                      onClick={closeSubmodules}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition-colors",
                        childActive
                          ? "bg-gray-200 text-gray-900"
                          : "text-gray-700 hover:bg-muted"
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-8 w-8 items-center justify-center rounded-md",
                          childActive
                              ? "bg-gray-200"
                            : "bg-gray-100"
                        )}
                      >
                        {child.icon}
                      </span>

                      <span className="font-medium">
                        {child.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-sm font-medium text-gray-500">
                  No submodules available
                </p>

                <p className="mt-1 text-xs text-gray-400">
                  This section doesn&apos;t have any
                  submodules.
                </p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* ========================================================= */}
      {/* NOTIFICATION PANEL                                        */}
      {/* ========================================================= */}

      <NotificationPanel
        open={activityOpen}
        onOpenChange={setActivityOpen}
        notifications={notificationsData?.notifications ?? []}
        unreadCount={unreadCount}
        isLoading={notificationsLoading}
      />

      {/* ========================================================= */}
      {/* SIDEBAR SPACER                                           */}
      {/* ========================================================= */}

      <div className="w-18 shrink-0" />

      {/* ========================================================= */}
      {/* MAIN CONTENT                                             */}
      {/* ========================================================= */}

      {/* `min-w-0` matters: this is a flex item in a row container, where the
          default `min-width: auto` stops it shrinking below its content's
          min-content width. A page containing a wide table (the search tables)
          would therefore force <main> wider than the space left beside the
          sidebar, pushing the whole layout out and giving the document a
          horizontal scrollbar. `flex-1` already fills the remaining width, so
          the previous `w-full` was redundant. */}
      <main className="min-h-screen min-w-0 flex-1">
        <BreadcrumbsWrapper>{children}</BreadcrumbsWrapper>
      </main>
    </div>
  );
}
